import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import {
  FiCheckCircle, FiUsers, FiBriefcase, FiFileText, FiBarChart2,
  FiMessageSquare, FiTool, FiCalendar, FiClock, FiAward,
  FiShield, FiZap, FiTrendingUp, FiSmartphone, FiLayers,
  FiArrowLeft, FiStar, FiGlobe, FiSend, FiCheck
} from "react-icons/fi";
import styles from "../styles/LandingPage.module.css";

/* ── Custom hooks ──────────────────────────────────────── */
function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.unobserve(el); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function useCounter(target, active, duration = 1500) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) return;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(ease * target));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [active, target, duration]);
  return val;
}

/* ── Data ──────────────────────────────────────────────── */
const features = [
  { icon: <FiBriefcase />, title: "إدارة المهام", desc: "تعيين المهام للموظفين، تتبع التقدم، وإشعارات فورية عبر واتساب عند كل تحديث.", color: "#4a90e2" },
  { icon: <FiUsers />, title: "إدارة الموظفين", desc: "سجل كامل للموظفين، الأدوار، الإجازات، وتقارير الأداء الشهرية والأسبوعية.", color: "#f5a623" },
  { icon: <FiGlobe />, title: "العملاء والحملات", desc: "متابعة العملاء وحملاتهم التسويقية من مكان واحد مع خطة عمل واضحة لكل حملة.", color: "#27ae60" },
  { icon: <FiFileText />, title: "الفواتير", desc: "إنشاء فواتير احترافية، إرسالها للعملاء، وتتبع حالة الدفع بكل سهولة.", color: "#8e44ad" },
  { icon: <FiBarChart2 />, title: "التقارير والإحصاءات", desc: "تقارير أداء الفريق، إحصاءات المبيعات، تقارير الحضور — كل شيء بنقرة واحدة.", color: "#e74c3c" },
  { icon: <FiMessageSquare />, title: "إشعارات واتساب", desc: "يتلقى كل موظف إشعاراً فورياً على واتساب عند تعيين مهمة جديدة أو تحديث الحالة.", color: "#25d366" },
  { icon: <FiCalendar />, title: "الحضور والإجازات", desc: "نظام حضور متكامل، طلبات الإجازة، وتقارير ساعات العمل الفعلية لكل موظف.", color: "#16a085" },
  { icon: <FiAward />, title: "المتدربون والإشراف", desc: "لوحة مهام خاصة بالمتدربين مع تقارير أداء ومتابعة دقيقة من المشرفين.", color: "#d35400" },
  { icon: <FiZap />, title: "أدوات احترافية", desc: "محول الصور، مُولّد QR Code، وأداة تفريغ الخلفيات بالذكاء الاصطناعي — داخل المنصة.", color: "#2980b9" },
  { icon: <FiTrendingUp />, title: "أداء الفريق", desc: "قياس إنتاجية كل عضو في الفريق مع تقارير مقارنة ومؤشرات أداء واضحة.", color: "#c0392b" },
  { icon: <FiClock />, title: "السجل اليومي", desc: "يُسجّل كل موظف إنجازات يومه، والمدير يتابع ما أنجزه الفريق في آخر كل يوم.", color: "#7f8c8d" },
  { icon: <FiLayers />, title: "الموارد الأسبوعية", desc: "مكتبة موارد الشركة وخطط العمل الأسبوعية للعملاء — منظّمة وسهلة الوصول.", color: "#1abc9c" },
];

const benefits = [
  { icon: <FiShield />, text: "بيانات آمنة ومحمية بالكامل مع Firebase" },
  { icon: <FiSmartphone />, text: "يعمل على جميع الأجهزة — موبايل، تابلت، كمبيوتر" },
  { icon: <FiCheckCircle />, text: "صلاحيات دقيقة لكل دور: مدير، موظف، متدرب، مشرف..." },
  { icon: <FiZap />, text: "سريع وخفيف — تحديثات فورية بدون تأخير" },
  { icon: <FiStar />, text: "واجهة بالعربية بالكامل، مريحة وسهلة الاستخدام" },
  { icon: <FiTrendingUp />, text: "يساعدك على قياس النمو واتخاذ قرارات مبنية على البيانات" },
];

const roles = [
  { label: "المدير", desc: "تحكم كامل بالفريق، العملاء، التقارير، والمالية", color: "#4a90e2" },
  { label: "مدير الحسابات", desc: "إدارة العملاء والحملات والفواتير والعمل الأسبوعي", color: "#f5a623" },
  { label: "المبيعات", desc: "متابعة العملاء، إنشاء الفواتير، وإدارة الحملات", color: "#27ae60" },
  { label: "الموارد البشرية", desc: "إدارة بيانات الموظفين، الحضور، والإجازات", color: "#8e44ad" },
  { label: "صانع المحتوى", desc: "متابعة مهام الحملات والخطط التسويقية", color: "#e74c3c" },
  { label: "المتدرب", desc: "لوحة مهام خاصة ومتابعة التقدم اليومي", color: "#16a085" },
];

const tickerItems = [
  "إدارة المهام", "إشعارات واتساب", "الفواتير", "تقارير الأداء",
  "إدارة العملاء", "QR Code", "تفريغ الخلفيات AI", "الحضور والإجازات",
  "المتدربون", "السجل اليومي", "محول الصور", "خطط الحملات",
];

const teamSizes = ["١-٥ موظفين", "٦-١٥ موظفاً", "١٦-٣٠ موظفاً", "٣١-٥٠ موظفاً", "٥٠+ موظف"];

/* ── Component ─────────────────────────────────────────── */
export default function LandingPage() {
  const [openFaq, setOpenFaq] = useState(null);

  // Form state
  const [form, setForm] = useState({ name: "", agency: "", whatsapp: "", email: "", teamSize: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Scroll-in refs
  const [heroStatsRef, heroStatsVisible] = useInView(0.3);
  const [featuresRef, featuresVisible] = useInView(0.05);
  const [benefitsRef, benefitsVisible] = useInView(0.05);
  const [rolesRef, rolesVisible] = useInView(0.05);
  const [toolsRef, toolsVisible] = useInView(0.1);
  const [faqRef, faqVisible] = useInView(0.1);
  const [formRef, formVisible] = useInView(0.1);

  // Animated counters
  const c12 = useCounter(12, heroStatsVisible);
  const c6 = useCounter(6, heroStatsVisible);
  const c100 = useCounter(100, heroStatsVisible);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError("");
    try {
      await addDoc(collection(db, "platform_requests"), {
        ...form,
        submittedAt: serverTimestamp(),
        status: "pending",
      });
      setSubmitted(true);
    } catch (err) {
      setSubmitError("حدث خطأ أثناء إرسال الطلب، يرجى المحاولة مجدداً.");
    } finally {
      setSubmitting(false);
    }
  };

  const faqs = [
    { q: "كيف أحصل على نسخة من المنصة لوكالتي؟", a: "فقط أرسل طلبك عبر الفورم أسفل الصفحة وسنتواصل معك خلال 24 ساعة لإعداد نسختك الخاصة وتهيئة البيانات." },
    { q: "هل يحتاج كل موظف لحساب خاص؟", a: "نعم، يُنشئ المدير حسابات للموظفين ويحدد دور كل واحد منهم، وبذلك يتحكم بما يراه كل موظف داخل المنصة." },
    { q: "كيف تعمل إشعارات واتساب؟", a: "كل موظف يُدخل رقم واتساب مرة واحدة، وبعدها يتلقى رسائل تلقائية عند تعيين مهمة جديدة له أو تغيير حالتها." },
    { q: "هل الأدوات مثل إزالة الخلفية تحتاج اتصال بالإنترنت؟", a: "ميزة تفريغ الصور تعمل بالكامل داخل المتصفح باستخدام الذكاء الاصطناعي — لا يتم رفع صورك لأي خادم خارجي." },
    { q: "هل يمكن استخدام المنصة من الجوال؟", a: "نعم، المنصة متجاوبة بالكامل وتعمل بشكل ممتاز على جميع الأجهزة." },
  ];

  return (
    <div className={styles.page} dir="rtl">

      {/* ─── Navbar ─── */}
      <nav className={styles.navbar}>
        <div className={styles.navInner}>
          <div className={styles.navBrand}>
            <div className={styles.navLogo}>AM</div>
            <span className={styles.navName}>Grow Up AM</span>
          </div>
          <div className={styles.navActions}>
            <a href="#request" className={styles.navCtaOutline}>اطلب نسختك</a>
            <Link to="/login" className={styles.navCtaGhost}>دخول العملاء <FiArrowLeft /></Link>
          </div>
        </div>
      </nav>

      {/* ─── Hero ─── */}
      <section className={styles.hero}>
        <div className={`${styles.orb} ${styles.orb1}`} />
        <div className={`${styles.orb} ${styles.orb2}`} />
        <div className={`${styles.orb} ${styles.orb3}`} />
        {[...Array(8)].map((_, i) => (
          <div key={i} className={styles.particle} style={{ "--pi": i }} />
        ))}

        <div className={styles.heroContent}>
          <div className={styles.heroBadge}>
            <FiZap style={{ verticalAlign: "middle", marginLeft: 6 }} />
            منصة إدارة الوكالات الاحترافية
          </div>
          <h1 className={styles.heroTitle}>
            كل ما تحتاجه لإدارة وكالتك
            <br />
            <span className={styles.heroAccent}>في مكان واحد</span>
          </h1>
          <p className={styles.heroSub}>
            Grow Up AM هي منصة متكاملة تساعد مديري الوكالات والفرق على إدارة المهام،
            العملاء، الموظفين، والفواتير — مع إشعارات واتساب فورية وتقارير دقيقة.
          </p>
          <div className={styles.heroCtas}>
            <a href="#request" className={styles.btnPrimary}>
              <span className={styles.btnShimmer} />
              اطلب نسختك الآن
            </a>
            <a href="#features" className={styles.btnOutline}>استكشف المميزات ↓</a>
          </div>

          <div className={styles.heroStats} ref={heroStatsRef}>
            <div className={styles.heroStat}>
              <span className={styles.heroStatNum}>{c12}+</span>
              <span className={styles.heroStatLabel}>وحدة متكاملة</span>
            </div>
            <div className={styles.heroStatDivider} />
            <div className={styles.heroStat}>
              <span className={styles.heroStatNum}>{c6}</span>
              <span className={styles.heroStatLabel}>أدوار مختلفة</span>
            </div>
            <div className={styles.heroStatDivider} />
            <div className={styles.heroStat}>
              <span className={styles.heroStatNum}>{c100}٪</span>
              <span className={styles.heroStatLabel}>عربي بالكامل</span>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Ticker ─── */}
      <div className={styles.tickerWrap}>
        <div className={styles.tickerTrack}>
          {[...tickerItems, ...tickerItems].map((item, i) => (
            <span key={i} className={styles.tickerItem}>
              <span className={styles.tickerDot} />
              {item}
            </span>
          ))}
        </div>
      </div>

      {/* ─── Features ─── */}
      <section className={styles.section} id="features">
        <div className={`${styles.sectionHeader} ${styles.fadeUp} ${featuresVisible ? styles.fadeUpVisible : ""}`}>
          <div className={styles.sectionDot} />
          <h2 className={styles.sectionTitle}>مميزات المنصة</h2>
          <p className={styles.sectionSub}>كل أداة صُممت لتوفير وقتك وتحسين أداء فريقك</p>
        </div>
        <div className={styles.featuresGrid} ref={featuresRef}>
          {features.map((f, i) => (
            <div
              key={i}
              className={`${styles.featureCard} ${featuresVisible ? styles.featureCardVisible : ""}`}
              style={{ "--delay": `${i * 60}ms` }}
            >
              <div className={styles.featureIcon} style={{ background: f.color + "18", color: f.color }}>
                {f.icon}
              </div>
              <h3 className={styles.featureTitle}>{f.title}</h3>
              <p className={styles.featureDesc}>{f.desc}</p>
              <div className={styles.featureGlow} style={{ background: f.color + "22" }} />
            </div>
          ))}
        </div>
      </section>

      {/* ─── Benefits ─── */}
      <section className={styles.benefitsSection}>
        <div className={styles.benefitsInner}>
          <div className={`${styles.sectionHeader} ${styles.fadeUp} ${benefitsVisible ? styles.fadeUpVisible : ""}`}>
            <div className={styles.sectionDot} style={{ background: "#f5a623" }} />
            <h2 className={styles.sectionTitle}>لماذا Grow Up AM؟</h2>
            <p className={styles.sectionSub}>مبني من الألف إلى الياء لاحتياجات الوكالات العربية</p>
          </div>
          <div className={styles.benefitsList} ref={benefitsRef}>
            {benefits.map((b, i) => (
              <div
                key={i}
                className={`${styles.benefitItem} ${benefitsVisible ? styles.benefitItemVisible : ""}`}
                style={{ "--delay": `${i * 80}ms` }}
              >
                <span className={styles.benefitIcon}>{b.icon}</span>
                <span className={styles.benefitText}>{b.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Roles ─── */}
      <section className={styles.section}>
        <div className={`${styles.sectionHeader} ${styles.fadeUp} ${rolesVisible ? styles.fadeUpVisible : ""}`}>
          <div className={styles.sectionDot} style={{ background: "#27ae60" }} />
          <h2 className={styles.sectionTitle}>مناسب لكل أعضاء الفريق</h2>
          <p className={styles.sectionSub}>صلاحيات مخصصة لكل دور — كل شخص يرى فقط ما يخصه</p>
        </div>
        <div className={styles.rolesGrid} ref={rolesRef}>
          {roles.map((r, i) => (
            <div
              key={i}
              className={`${styles.roleCard} ${rolesVisible ? styles.roleCardVisible : ""}`}
              style={{ "--delay": `${i * 70}ms`, borderTopColor: r.color }}
            >
              <div className={styles.roleEmoji} style={{ color: r.color }}><FiUsers /></div>
              <div className={styles.roleLabel} style={{ color: r.color }}>{r.label}</div>
              <p className={styles.roleDesc}>{r.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ─── Tools ─── */}
      <section className={styles.toolsSection}>
        <div className={styles.toolsInner}>
          <div className={`${styles.sectionHeader} ${styles.fadeUp} ${toolsVisible ? styles.fadeUpVisible : ""}`}>
            <div className={styles.sectionDot} style={{ background: "#fff" }} />
            <h2 className={styles.sectionTitle} style={{ color: "#fff" }}>أدوات احترافية مدمجة</h2>
            <p className={styles.sectionSub} style={{ color: "rgba(255,255,255,0.72)" }}>لا تحتاج لأدوات خارجية بعد الآن</p>
          </div>
          <div className={styles.toolsCards} ref={toolsRef}>
            {[
              { icon: <FiTool />, title: "محول الصور", desc: "تحويل الصور بين الصيغ المختلفة، تغيير الأبعاد، وضبط الجودة — دفعة واحدة.", tag: "PNG · JPG · WEBP" },
              { icon: <FiLayers />, title: "مُولّد QR Code", desc: "إنشاء QR codes بألوان مخصصة، لوجو، وإعدادات احترافية — مع حفظها للمراجعة لاحقاً.", tag: "URL · Text · Wi-Fi" },
              { icon: <FiZap />, title: "تفريغ الخلفيات AI", desc: "إزالة خلفية أي صورة بذكاء اصطناعي داخل المتصفح مباشرةً — سريع وخصوصي 100٪.", tag: "AI · محلي · سريع" },
            ].map((t, i) => (
              <div
                key={i}
                className={`${styles.toolCard} ${toolsVisible ? styles.toolCardVisible : ""}`}
                style={{ "--delay": `${i * 100}ms` }}
              >
                <div className={styles.toolCardIcon}>{t.icon}</div>
                <span className={styles.toolTag}>{t.tag}</span>
                <h3>{t.title}</h3>
                <p>{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section className={styles.section}>
        <div className={`${styles.sectionHeader} ${styles.fadeUp} ${faqVisible ? styles.fadeUpVisible : ""}`}>
          <div className={styles.sectionDot} style={{ background: "#8e44ad" }} />
          <h2 className={styles.sectionTitle}>أسئلة شائعة</h2>
        </div>
        <div className={styles.faqList} ref={faqRef}>
          {faqs.map((f, i) => (
            <div
              key={i}
              className={`${styles.faqItem} ${faqVisible ? styles.faqItemVisible : ""}`}
              style={{ "--delay": `${i * 80}ms` }}
            >
              <button
                className={`${styles.faqQ} ${openFaq === i ? styles.faqQOpen : ""}`}
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
              >
                <span>{f.q}</span>
                <span className={`${styles.faqArrow} ${openFaq === i ? styles.faqArrowOpen : ""}`}>▼</span>
              </button>
              <div className={`${styles.faqA} ${openFaq === i ? styles.faqAOpen : ""}`}>
                <div className={styles.faqAInner}>{f.a}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ─── Request Form ─── */}
      <section className={styles.requestSection} id="request">
        {/* background orbs */}
        <div className={styles.reqOrb1} />
        <div className={styles.reqOrb2} />

        <div
          className={`${styles.requestInner} ${styles.fadeUp} ${formVisible ? styles.fadeUpVisible : ""}`}
          ref={formRef}
        >
          {/* Left: pitch */}
          <div className={styles.requestPitch}>
            <div className={styles.pitchBadge}>
              <FiSend style={{ marginLeft: 6 }} />
              احصل على نسختك
            </div>
            <h2 className={styles.pitchTitle}>
              جاهز تحول وكالتك
              <br />
              <span className={styles.pitchAccent}>لمستوى أعلى؟</span>
            </h2>
            <p className={styles.pitchSub}>
              أرسل لنا طلبك وسنتواصل معك خلال <strong>24 ساعة</strong> لإعداد
              نسخة كاملة من المنصة مخصصة لوكالتك أو شركتك.
            </p>
            <div className={styles.pitchPoints}>
              {[
                "نسخة مخصصة باسم وكالتك",
                "إعداد كامل للحسابات والأدوار",
                "دعم فني مباشر بعد التسليم",
                "تدريب الفريق على استخدام المنصة",
              ].map((p, i) => (
                <div key={i} className={styles.pitchPoint}>
                  <span className={styles.pitchCheck}><FiCheck /></span>
                  <span>{p}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: form */}
          <div className={styles.formCard}>
            {submitted ? (
              <div className={styles.successState}>
                <div className={styles.successIcon}>
                  <FiCheck />
                </div>
                <h3 className={styles.successTitle}>تم إرسال طلبك!</h3>
                <p className={styles.successSub}>
                  وصلنا طلبك بنجاح، سنتواصل معك على واتساب أو البريد خلال 24 ساعة.
                </p>
                <button
                  className={styles.resetBtn}
                  onClick={() => { setSubmitted(false); setForm({ name: "", agency: "", whatsapp: "", email: "", teamSize: "", message: "" }); }}
                >
                  إرسال طلب آخر
                </button>
              </div>
            ) : (
              <>
                <div className={styles.formHeader}>
                  <h3 className={styles.formTitle}>أرسل طلبك الآن</h3>
                  <p className={styles.formSub}>الحقول المشار إليها بـ * مطلوبة</p>
                </div>

                <form className={styles.form} onSubmit={handleSubmit}>
                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <label className={styles.label}>الاسم الكامل *</label>
                      <input
                        className={styles.input}
                        type="text"
                        name="name"
                        placeholder="محمد أحمد"
                        value={form.name}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label className={styles.label}>اسم الوكالة / الشركة *</label>
                      <input
                        className={styles.input}
                        type="text"
                        name="agency"
                        placeholder="Grow Up Agency"
                        value={form.agency}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <label className={styles.label}>رقم واتساب *</label>
                      <input
                        className={styles.input}
                        type="tel"
                        name="whatsapp"
                        placeholder="+970 59 000 0000"
                        value={form.whatsapp}
                        onChange={handleChange}
                        required
                        dir="ltr"
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label className={styles.label}>البريد الإلكتروني *</label>
                      <input
                        className={styles.input}
                        type="email"
                        name="email"
                        placeholder="info@agency.com"
                        value={form.email}
                        onChange={handleChange}
                        required
                        dir="ltr"
                      />
                    </div>
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>عدد الموظفين تقريباً</label>
                    <select
                      className={styles.input}
                      name="teamSize"
                      value={form.teamSize}
                      onChange={handleChange}
                    >
                      <option value="">اختر...</option>
                      {teamSizes.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>رسالة إضافية (اختياري)</label>
                    <textarea
                      className={`${styles.input} ${styles.textarea}`}
                      name="message"
                      placeholder="أي تفاصيل أو متطلبات خاصة..."
                      value={form.message}
                      onChange={handleChange}
                      rows={3}
                    />
                  </div>

                  {submitError && (
                    <div className={styles.errorMsg}>{submitError}</div>
                  )}

                  <button
                    type="submit"
                    className={styles.submitBtn}
                    disabled={submitting}
                  >
                    {submitting ? (
                      <span className={styles.spinner} />
                    ) : (
                      <>
                        <span className={styles.btnShimmer} />
                        <FiSend style={{ marginLeft: 8 }} />
                        إرسال الطلب
                      </>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer className={styles.footer}>
        <div className={styles.footerBrand}>
          <div className={styles.navLogo} style={{ width: 32, height: 32, fontSize: 13 }}>AM</div>
          <span>Grow Up AM</span>
        </div>
        <p className={styles.footerNote}>منصة إدارة الوكالات — جميع الحقوق محفوظة © {new Date().getFullYear()}</p>
        <Link to="/login" className={styles.footerLogin}>دخول العملاء الحاليين ←</Link>
      </footer>
    </div>
  );
}
